import {
  Box,
  Button,
  Drawer,
  Link,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { DataGrid, type GridColumns, GridOverlay } from '@mui/x-data-grid'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import { CLUSTER_LIST_PAGE_SIZE } from 'components/configurations/clusters/clusters-utils'
import { OwnedClusterNamespacesDrawerContent } from 'components/configurations/clusters/OwnedClusterNamespacesDrawerContent'
import Icon from 'components/Icon'
import useAuth from 'hooks/use-auth'
import { useDrawerControlForDataGrid } from 'hooks/use-drawer-control-for-data-grid'
import { useManageSharedAssets } from 'hooks/use-manage-shared-assets'
import { muiTheme } from 'mui-theme'
import {
  sharedAssetsRequestsApprovePost,
  sharedAssetsRequestsDenyPost
} from 'utils/endpoints-access'
import {
  defaultColumnProps,
  defaultGridProps,
  getGridStyles
} from 'utils/mui-components/data-grid-utils'

import type { OwnedK8sClusterAsset } from 'types/api/sharedAssets/sharedAssets'

const OwnedClusterListEmptyState: React.FC<{ isFiltered: boolean }> = ({
  isFiltered
}) => {
  const message = isFiltered
    ? "We couldn't find any clusters that match the search criteria"
    : "We couldn't find any clusters owned by this team"

  return (
    <GridOverlay>
      <Typography>{message}</Typography>
    </GridOverlay>
  )
}

export const OwnedClusterList: React.FC<{
  ownedClusters: OwnedK8sClusterAsset[]
  isFiltered: boolean
}> = ({ ownedClusters, isFiltered }) => {
  const navigate = useNavigate()
  const { currentTeam, hasEndpointAccessForAll } = useAuth()

  const { getPendingIncomingRequestsForCluster } = useManageSharedAssets()

  const {
    isOpen,
    activeItem: activeAsset,
    handleRowClick,
    handleOpen,
    handleClose
  } = useDrawerControlForDataGrid<OwnedK8sClusterAsset>()

  const columns: GridColumns<OwnedK8sClusterAsset> = [
    {
      ...defaultColumnProps,
      field: 'isOutdated',
      headerName: 'Status',
      flex: 0.75,
      renderCell: ({ row }) => {
        const { isOutdated } = row

        return (
          <Typography
            color={isOutdated ? 'error.main' : 'success.main'}
            variant="inherit"
          >
            {isOutdated ? 'Outdated' : 'Active'}
          </Typography>
        )
      }
    },
    {
      ...defaultColumnProps,
      field: 'clusterName',
      headerName: 'Cluster',
      flex: 2,
      renderCell: ({ row }) => {
        const { clusterName } = row
        const pendingRequests = getPendingIncomingRequestsForCluster(
          row.assetId
        )

        return (
          <Link onClick={() => handleOpen(row)}>
            <Stack alignItems="center" direction="row" sx={{ columnGap: 0.5 }}>
              {pendingRequests.length > 0 && (
                <Tooltip
                  placement="top"
                  title="A team is waiting for namespace sharing approval"
                >
                  <Icon
                    color={muiTheme.palette.primary.main}
                    data-testid="pending-cluster-access-request-icon"
                    icon="solidCirclesOverlap"
                  />
                </Tooltip>
              )}
              <TruncatedTextWithTooltip
                tooltipProps={{ title: clusterName }}
                variant="inherit"
              >
                {clusterName}
              </TruncatedTextWithTooltip>
            </Stack>
          </Link>
        )
      }
    },
    {
      ...defaultColumnProps,
      field: 'owningTeamId',
      headerName: 'Owning Team',
      flex: 2,
      renderCell: () => {
        return (
          <TruncatedTextWithTooltip
            tooltipProps={{ title: currentTeam.name }}
            variant="inherit"
          >
            {currentTeam.name}
          </TruncatedTextWithTooltip>
        )
      }
    },
    {
      ...defaultColumnProps,
      field: 'actions',
      headerName: 'Actions',
      flex: 1.25,
      renderCell: ({ row }) => {
        return (
          <Button
            sx={{ width: 'fit-content' }}
            variant="contained"
            onClick={() => {
              navigate(`/configurations/clusters/manage/${row.assetId}`)
            }}
          >
            Manage
          </Button>
        )
      }
    }
  ]

  return (
    <Box data-testid="owned-cluster-list">
      <Drawer
        anchor="right"
        open={isOpen}
        PaperProps={{
          sx: {
            padding: 6,
            height: '100%',
            width: 600
          }
        }}
        onClose={handleClose}
      >
        {activeAsset && (
          <OwnedClusterNamespacesDrawerContent activeAsset={activeAsset} />
        )}
      </Drawer>
      <DataGrid
        {...defaultGridProps}
        columns={columns}
        columnVisibilityModel={{
          actions: hasEndpointAccessForAll([
            sharedAssetsRequestsApprovePost,
            sharedAssetsRequestsDenyPost
          ])
        }}
        components={{
          NoRowsOverlay: () => (
            <OwnedClusterListEmptyState isFiltered={isFiltered} />
          )
        }}
        getRowId={(value) => value.assetId}
        pageSize={CLUSTER_LIST_PAGE_SIZE}
        rows={ownedClusters}
        rowsPerPageOptions={[CLUSTER_LIST_PAGE_SIZE]}
        sx={getGridStyles()}
        onRowClick={handleRowClick}
      />
    </Box>
  )
}
